import {
    Box,
    Button,
    Checkbox,
    Fieldset,
    Group,
    Input,
    JsonInput,
    LoadingOverlay,
    MultiSelect,
    NumberInput,
    SegmentedControl,
    Select,
    Slider,
    Stack,
    Text,
    Textarea,
    TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import type { JSX } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import Hider from "../Hider";
import { useSiteConfig } from "../SiteConfig";
import TagsInput from "../TagsInput";
import {
    LANGUAGES,
    type Language,
    languageLabel,
    type ReasoningEffortType,
    reasoningEffortLabel,
    VERBOSITIES,
    type Verbosity,
    verbosityLabel,
} from "../utils";
import classes from "./AiPersonalityForm.module.css";

export interface IFormValues {
    name: string;
    externalName: string | null;
    temperature: number | null;
    systemPrompt: string;
    includeRetrieval: boolean;
    includeBrainstorm: boolean;
    epsilon: number;
    searchPrompt: string | null;
    tags: string[];
    languages: Language[];
    model: string;
    useCache: boolean;
    showInChat: boolean;
    reasoningEffort: ReasoningEffortType | null;
    verbosity: Verbosity | null;
    useResponseFormat: boolean;
    responseFormat: string | null;
    maxIterations: number;
}

interface IResponseFormatProperty {
    name: string;
    value: string;
}

interface IInnerFormValues {
    name: string;
    externalName: string;
    temperature: number;
    systemPrompt: string;
    includeRetrieval: boolean;
    includeBrainstorm: boolean;
    epsilon: number;
    searchPrompt: string;
    tags: string[];
    languages: Language[];
    model: string;
    useCache: boolean;
    showInChat: boolean;
    reasoningEffort: ReasoningEffortType;
    verbosity: Verbosity;
    useResponseFormat: boolean;
    responseFormat: string;
    responseFormatProperties: IResponseFormatProperty[];
    maxIterations: number;
}

interface IProps {
    handleSave: (values: IFormValues) => void;
    handleTest?: (values: IFormValues) => void;
    testScore?: number | null;
    initialValues?: IFormValues;
    fetching: boolean;
    hideName?: boolean;
    testFetching?: boolean;
    hideShowInChat?: boolean;
    onFormChange?: (values: IFormValues) => void;
}

function getEffortOptions(support: string): { label: string; value: ReasoningEffortType }[] {
    if (support === "extended") {
        return ["MINIMAL", "LOW", "MEDIUM", "HIGH"].map((eff) => ({
            label: reasoningEffortLabel(eff as ReasoningEffortType),
            value: eff as ReasoningEffortType,
        }));
    }
    if (support === "basic") {
        return ["LOW", "MEDIUM", "HIGH"].map((eff) => ({
            label: reasoningEffortLabel(eff as ReasoningEffortType),
            value: eff as ReasoningEffortType,
        }));
    }
    return [];
}

const VERBOSITY_OPTIONS = VERBOSITIES.map((v) => ({ label: verbosityLabel(v), value: v }) as const);

function fmtSchemaName(name: string): string {
    return `${name.replace(/[^a-zA-Z0-9]/g, "_")}_response_schema`;
}

function buildResponseFormatFromProperties(name: string, properties: IResponseFormatProperty[]): string {
    const schemaName = fmtSchemaName(name);
    const propertiesObj: Record<string, any> = {};
    const required: string[] = [];

    for (const prop of properties) {
        if (prop.name) {
            propertiesObj[prop.name] = JSON.parse(prop.value);
            required.push(prop.name);
        }
    }

    const schema = {
        type: "json_schema",
        name: schemaName,
        strict: true,
        schema: {
            type: "object",
            properties: propertiesObj,
            required: required,
            additionalProperties: false,
        },
    };

    return JSON.stringify(schema, null, 2);
}

function parsePropertiesFromResponseFormat(responseFormat: string | null): IResponseFormatProperty[] {
    if (!responseFormat) {
        return [];
    }

    try {
        const parsed = JSON.parse(responseFormat);
        const schema = parsed.schema;
        const properties = schema?.properties || {};

        return Object.entries(properties).map(([name, value]) => ({
            name,
            value: JSON.stringify(value, null, 2),
        }));
    } catch (e) {
        console.error("Error parsing response format:", e);
        return [];
    }
}

const DEFAULT_SEARCH_PROMPT =
    "You are a hypothetical document generator. Given the SEARCH QUERY and CONTEXT FOR SEARCH write a document that would help answer the query. Limit your output to one paragraph.";

function AiPersonalityForm({
    handleSave,
    initialValues,
    fetching,
    hideName,
    handleTest,
    testScore,
    testFetching,
    hideShowInChat,
    onFormChange,
}: IProps): JSX.Element {
    const [submitAction, setSubmitAction] = useState<"save" | "test">("save");
    const config = useSiteConfig();

    const tranformValues = useCallback(
        (values: IInnerFormValues): IFormValues => {
            const modelConfig = config.chatModels.find((model) => model.name === values.model);
            if (!modelConfig) {
                throw new Error(`Model ${values.model} not found`);
            }

            let responseFormat = null;
            if (values.useResponseFormat && values.responseFormatProperties.length > 0) {
                responseFormat = buildResponseFormatFromProperties(values.name, values.responseFormatProperties);
            }

            return {
                ...values,
                externalName: values.externalName || null,
                searchPrompt: values.includeRetrieval ? values.searchPrompt || null : null,
                temperature: modelConfig.temperature ? values.temperature : null,
                reasoningEffort: modelConfig.reasoningSupport !== "none" ? values.reasoningEffort || null : null,
                verbosity: modelConfig.verbosity ? values.verbosity || null : null,
                responseFormat: responseFormat,
                maxIterations: values.maxIterations,
            };
        },
        [config.chatModels],
    );

    const handleSubmit = (values: IInnerFormValues) => {
        const newValues = tranformValues(values);
        if (submitAction === "test" && handleTest) {
            return handleTest(newValues);
        }
        if (submitAction === "save") {
            return handleSave(newValues);
        }
    };

    const initialProperties = useMemo(() => {
        return parsePropertiesFromResponseFormat(initialValues?.responseFormat ?? null);
    }, [initialValues?.responseFormat]);

    const form = useForm<IInnerFormValues>({
        initialValues: {
            name: initialValues?.name ?? "",
            externalName: initialValues?.externalName ?? "",
            temperature: initialValues?.temperature ?? 0.5,
            systemPrompt: initialValues?.systemPrompt ?? "",
            includeRetrieval: initialValues?.includeRetrieval ?? false,
            includeBrainstorm: initialValues?.includeBrainstorm ?? false,
            epsilon: initialValues?.epsilon ?? 0.0,
            searchPrompt: initialValues?.searchPrompt || DEFAULT_SEARCH_PROMPT,
            tags: initialValues?.tags ?? [],
            languages: initialValues?.languages ?? [],
            model: initialValues?.model ?? "",
            useCache: initialValues?.useCache ?? false,
            showInChat: initialValues?.showInChat ?? true,
            reasoningEffort: initialValues?.reasoningEffort ?? "MEDIUM",
            useResponseFormat: !!initialValues?.responseFormat,
            maxIterations: initialValues?.maxIterations ?? 1,
            responseFormat: initialValues?.responseFormat ?? "",
            responseFormatProperties: initialProperties,
            verbosity: initialValues?.verbosity ?? "MEDIUM",
        },
        validate: {
            name: (value) => (value ? null : "Required"),
            systemPrompt: (value) => (value ? null : "Required"),
            model: (value) => (value ? null : "Required"),
            searchPrompt: (value, values) => (values.includeRetrieval && !value ? "Required" : null),
            tags: (value, values) =>
                values.includeRetrieval && (!value || value.length === 0)
                    ? "At least one tag is required when retrieval is enabled"
                    : null,
            maxIterations: (value, values) => {
                const needsIterations = values.includeRetrieval || values.includeBrainstorm;
                if (needsIterations) {
                    return value >= 2 ? null : "Must be at least 2 when retrieval or brainstorming enabled";
                }
                return value === 1 ? null : "Must be 1 when neither retrieval nor brainstorming is enabled";
            },
            epsilon: (value) => (value >= 0 && value <= 1 ? null : "Must be between 0.0 and 1.0"),
            responseFormatProperties: (properties, values) => {
                if (values.useResponseFormat) {
                    // Check for duplicate property names
                    const names = properties.map((p) => p.name);
                    const uniqueNames = new Set(names);
                    if (names.length !== uniqueNames.size) {
                        return "Property names must be unique";
                    }

                    // Validate each property value is valid JSON
                    for (const prop of properties) {
                        if (!prop.name) {
                            return "All properties must have a name";
                        }

                        try {
                            JSON.parse(prop.value);
                        } catch (e) {
                            return `Invalid JSON in property '${prop.name}': ${(e as Error).message}`;
                        }
                    }
                }

                return null;
            },
        },
    });

    useEffect(() => {
        if (onFormChange) {
            onFormChange(tranformValues(form.values));
        }
    }, [form.values, onFormChange, tranformValues]);

    useEffect(() => {
        if (form.values.useResponseFormat && form.values.responseFormatProperties.length === 0) {
            form.setFieldValue("responseFormatProperties", [
                { name: "", value: JSON.stringify({ type: "string", description: "" }, null, 2) },
            ]);
        }
    }, [form.values.useResponseFormat, form.values.responseFormatProperties.length, form.setFieldValue]);

    const modelConfig = config.chatModels.find((model) => model.name === form.values.model);
    const showTemperature = !!modelConfig?.temperature;
    const reasoningSupport = modelConfig?.reasoningSupport ?? "none";
    const showResoningEffort = reasoningSupport !== "none";
    const showVerbosity = !!modelConfig?.verbosity;

    const allowedEffortOptions = useMemo(() => getEffortOptions(reasoningSupport), [reasoningSupport]);

    useEffect(() => {
        if (!showResoningEffort) return;
        const allowed = allowedEffortOptions.map((o) => o.value);
        const current = form.values.reasoningEffort;
        if (!allowed.includes(current)) {
            const fallback = (
                allowed.includes("MEDIUM" as ReasoningEffortType) ? "MEDIUM" : allowed[0]
            ) as ReasoningEffortType;
            form.setFieldValue("reasoningEffort", fallback);
        }
    }, [showResoningEffort, allowedEffortOptions, form.values.reasoningEffort, form.setFieldValue]);

    return (
        <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack maw={800} mx="auto">
                <LoadingOverlay visible={fetching} />
                <Hider show={!hideName}>
                    <TextInput withAsterisk label="Name" {...form.getInputProps("name")} />
                </Hider>
                <Hider show={!hideName}>
                    <TextInput label="External name" {...form.getInputProps("externalName")} />
                </Hider>
                <Hider show={!hideShowInChat}>
                    <Checkbox label="Show in chat" {...form.getInputProps("showInChat", { type: "checkbox" })} />
                </Hider>
                <Select
                    label="AI model"
                    placeholder="Select model for AI personality"
                    data={config.chatModels.map((model) => model.name)}
                    {...form.getInputProps("model")}
                />
                <Textarea
                    withAsterisk
                    autosize
                    minRows={3}
                    label="Personality prompt"
                    {...form.getInputProps("systemPrompt")}
                />
                {showTemperature && (
                    <Text size="sm" component="label">
                        Randomness
                        <Slider
                            min={0.0}
                            max={1.0}
                            step={0.01}
                            value={form.getInputProps("temperature").value}
                            onChange={form.getInputProps("temperature").onChange}
                        />
                    </Text>
                )}
                {showResoningEffort && (
                    <Input.Wrapper label="Reasoning effort" classNames={{ label: classes.inputLabel }} mb="sm">
                        <SegmentedControl
                            value={form.values.reasoningEffort}
                            onChange={(v) => form.setFieldValue("reasoningEffort", v as ReasoningEffortType)}
                            data={allowedEffortOptions}
                        />
                    </Input.Wrapper>
                )}
                {showVerbosity && (
                    <Input.Wrapper label="Verbosity" classNames={{ label: classes.inputLabel }} mb="sm">
                        <SegmentedControl
                            value={form.values.verbosity}
                            onChange={(v) => form.setFieldValue("verbosity", v as Verbosity)}
                            data={VERBOSITY_OPTIONS}
                        />
                    </Input.Wrapper>
                )}
                <NumberInput min={1} step={1} label="Max iterations" {...form.getInputProps("maxIterations")} />
                <Checkbox
                    label="Include knowledge from database"
                    {...form.getInputProps("includeRetrieval", { type: "checkbox" })}
                />
                <Hider show={form.values.includeRetrieval}>
                    <Fieldset legend="Configure Knowledge Database search">
                        <Text size="sm" mb="md">
                            This provides the AI with two tools: documentSearch for searching the Knowledge Database,
                            and readDocument for reading specific parts of documents. It is recommended to mention these
                            tools and what they are useful for in the personality prompt.
                        </Text>
                        <Textarea autosize label="Search prompt" minRows={3} {...form.getInputProps("searchPrompt")} />
                        <TagsInput form={form} />
                        <MultiSelect
                            label="Languages"
                            data={LANGUAGES.map((lang) => ({ value: lang, label: languageLabel(lang) }))}
                            clearable
                            {...form.getInputProps("languages")}
                        />
                    </Fieldset>
                </Hider>
                <Checkbox
                    label="Include brainstorming tool"
                    {...form.getInputProps("includeBrainstorm", { type: "checkbox" })}
                />
                <Hider show={form.values.includeBrainstorm}>
                    <Fieldset legend="Configure Brainstorm Tool">
                        <Text size="sm" mb="md">
                            Enable an idea-generation tool. Epsilon controls exploration vs. focus: 0.0 = focused, 1.0 =
                            highly exploratory.
                        </Text>
                        <Text size="sm" component="label">
                            Epsilon (exploration factor)
                            <Slider
                                min={0}
                                max={1}
                                step={0.01}
                                value={form.getInputProps("epsilon").value}
                                onChange={form.getInputProps("epsilon").onChange}
                            />
                        </Text>
                    </Fieldset>
                </Hider>
                <Checkbox label="Cache responses" {...form.getInputProps("useCache", { type: "checkbox" })} />

                <Checkbox
                    label="Use JSON response format"
                    {...form.getInputProps("useResponseFormat", { type: "checkbox" })}
                />
                <Hider show={form.values.useResponseFormat}>
                    <Fieldset legend="Response Format">
                        <Text size="sm" mb="md">
                            Define properties for structured JSON responses. Each property will be automatically added
                            to the required list.
                        </Text>

                        {form.values.responseFormatProperties.map((_, index) => (
                            <Box key={index} className={classes.responseFormatProperty}>
                                <Group mb="xs">
                                    <TextInput
                                        label="Property Name"
                                        placeholder="e.g., summary, analysis, recommendation"
                                        style={{ flex: 1 }}
                                        {...form.getInputProps(`responseFormatProperties.${index}.name`)}
                                    />
                                    <Box mt={25}>
                                        <Group>
                                            <Button
                                                color="blue"
                                                variant="outline"
                                                size="xs"
                                                disabled={index === 0}
                                                onClick={() => {
                                                    form.reorderListItem("responseFormatProperties", {
                                                        from: index,
                                                        to: index - 1,
                                                    });
                                                }}
                                            >
                                                Move Up
                                            </Button>
                                            <Button
                                                color="blue"
                                                variant="outline"
                                                size="xs"
                                                disabled={index === form.values.responseFormatProperties.length - 1}
                                                onClick={() => {
                                                    form.reorderListItem("responseFormatProperties", {
                                                        from: index,
                                                        to: index + 1,
                                                    });
                                                }}
                                            >
                                                Move Down
                                            </Button>
                                            <Button
                                                color="red"
                                                variant="outline"
                                                size="xs"
                                                onClick={() => {
                                                    form.removeListItem("responseFormatProperties", index);
                                                }}
                                            >
                                                Remove
                                            </Button>
                                        </Group>
                                    </Box>
                                </Group>
                                <JsonInput
                                    label="Property Definition (JSON)"
                                    placeholder='e.g., { "type": "string", "description": "A summary of the content" }'
                                    formatOnBlur
                                    autosize
                                    minRows={3}
                                    {...form.getInputProps(`responseFormatProperties.${index}.value`)}
                                />
                            </Box>
                        ))}

                        <Button
                            mt="md"
                            variant="outline"
                            onClick={() => {
                                form.insertListItem("responseFormatProperties", {
                                    name: "",
                                    value: JSON.stringify({ type: "string", description: "" }, null, 2),
                                });
                            }}
                        >
                            Add Property
                        </Button>

                        <Text size="xs" mt="md" c="dimmed">
                            The JSON schema will be automatically generated with all properties marked as required.
                        </Text>
                    </Fieldset>
                </Hider>

                <Group justify="flex-end" mt="md">
                    {!!handleTest && (
                        <>
                            {testScore !== undefined && testScore !== null && <Box>Avg score: {testScore}</Box>}
                            <Button type="submit" disabled={testFetching} onClick={() => setSubmitAction("test")}>
                                Test
                            </Button>
                        </>
                    )}
                    <Button type="submit" disabled={fetching} onClick={() => setSubmitAction("save")}>
                        Save
                    </Button>
                </Group>
            </Stack>
        </form>
    );
}

export default AiPersonalityForm;
